import { FRAME_MS, drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { ActResult, CasinoGame, TableEnv } from './types';

/**
 * Big Six Wheel (the "money wheel"): 54 segments. Bet on a segment type; if
 * the wheel stops on it, it pays about its face value. Real wheels pay face
 * value (11-24% edges); ours pays a little over, so every segment returns
 * ~96% (raised 2026-09-23 from 76-89% to ~93-94%, then to ~96% in the
 * balance patch: user, lift the worst normal tables to ~96%).
 *
 * The stop is drawn when you spin; the wheel then scrolls down past the
 * pointer on the lens (a vertical strip, by request) and slows to a stop
 * over SPIN_MS (driven by the real-time tick, like Horse Racing), settling
 * when it stops. You're locked in until then. The wheel stays where it
 * stopped for the next spin.
 */
export type Segment = '1' | '2' | '5' | '10' | '20' | 'joker' | 'logo';

export const SEGMENTS: readonly { id: Segment; label: string; count: number; pays: number }[] = [
  { id: '1', label: '$1', count: 24, pays: 1.16 },
  { id: '2', label: '$2', count: 15, pays: 2.46 },
  { id: '5', label: '$5', count: 7, pays: 6.4 },
  { id: '10', label: '$10', count: 4, pays: 11.95 },
  { id: '20', label: '$20', count: 2, pays: 24.9 },
  { id: 'joker', label: 'Joker', count: 1, pays: 50.8 },
  { id: 'logo', label: 'Logo', count: 1, pays: 50.8 },
];

export const WHEEL_SIZE = SEGMENTS.reduce((s, x) => s + x.count, 0);

/** The wheel itself: every segment repeated by its count, spread around the
 *  rim with a stride coprime to 54 so the same kind rarely sits together. */
export const WHEEL: readonly Segment[] = (() => {
  const pool: Segment[] = [];
  for (const s of SEGMENTS) for (let i = 0; i < s.count; i++) pool.push(s.id);
  const stride = 11;
  return pool.map((_, i) => pool[(i * stride) % pool.length]!);
})();


export const SPIN_MS = 3000;
/** Whole turns the wheel makes before its final approach. */
export const LAPS = 1;

export function bigSixReturn(bet: Segment, landed: Segment, stake: number): number {
  if (bet !== landed) return 0;
  return Math.round(stake * (1 + SEGMENTS.find((s) => s.id === bet)!.pays));
}

/** A bet's results (for the ★'s second-chance rate). */
export function bigSixOutcomes(bet: Segment): Outcome[] {
  const seg = SEGMENTS.find((s) => s.id === bet)!;
  const p = seg.count / WHEEL_SIZE;
  return [
    [p, 1 + seg.pays],
    [1 - p, 0],
  ];
}

export interface BigSixState {
  phase: 'idle' | 'spinning' | 'done';
  bet: Segment;
  /** Wheel position (index into WHEEL) the spin started from / stops at. */
  from: number;
  stop: number;
  startedAt: number;
  /** Segments scrolled past so far; only changes redraw. */
  shown: number;
  last: Segment | null;
  lastStake: number;
  lastBet: Segment;
  returned: number;
  /** Joker hits in a row while betting Joker (the jackpot needs two). */
  jokerStreak: number;
  history: Segment[];
  /** The ★'s second spin turned this one around. */
  lucky: boolean;
}

const label = (id: Segment) => SEGMENTS.find((s) => s.id === id)!.label;

/** Segments between `from` and the stop, going round LAPS times first. */
function travel(from: number, stop: number): number {
  return LAPS * WHEEL_SIZE + ((stop - from + WHEEL_SIZE) % WHEEL_SIZE);
}

/** Five segments stacked around the pointer at wheel position `at`, the
 *  middle one marked. The next segments sit above the pointer, so as `at`
 *  grows the wheel scrolls downward (top to bottom, by request). */
export function wheelView(at: number): string[] {
  if (!Number.isFinite(at)) at = 0; // a table saved before the wheel animated
  return [2, 1, 0, -1, -2].map((o) => {
    const text = label(WHEEL[(((at + o) % WHEEL_SIZE) + WHEEL_SIZE) % WHEEL_SIZE]!);
    return o === 0 ? `  > ${text} <` : `    ${text}`;
  });
}

/** Casino frames of a spin from `from` to `to` (for the losing first spin
 *  before a ★ second chance), eased like the real one. */
function spinToFrames(from: number, to: number, bet: Segment): string[] {
  const n = 8;
  return Array.from({ length: n }, (_, f) => {
    const t = (f + 1) / (n + 1);
    return [`Betting ${label(bet)}`, ...wheelView(from + Math.round(travel(from, to) * (1 - (1 - t) ** 3)))].join('\n');
  });
}

function settle(state: BigSixState, env: TableEnv): ActResult<BigSixState> {
  const landed = WHEEL[state.stop]!;
  const returned = bigSixReturn(state.lastBet, landed, state.lastStake);
  const jokerStreak = state.lastBet === 'joker' && landed === 'joker' ? state.jokerStreak + 1 : 0;
  return {
    state: { ...state, phase: 'done', from: state.stop, last: landed, returned, jokerStreak, history: [landed, ...state.history].slice(0, 8) },
    bankroll: env.bankroll + returned,
    settled: { wagered: state.lastStake, returned },
    jackpot: jokerStreak >= 2,
  };
}

export const bigSix: CasinoGame<BigSixState> = {
  id: 'bigsix',
  usesLuck: true,
  name: 'Big Six Wheel',
  blurb: 'Pick a segment; the wheel pays about its face value.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a losing spin sometimes spins again.',
  jackpotRule: 'Hit the Joker twice in a row betting on it.',
  lockedHint: '(the wheel is spinning)',
  create: () => ({ phase: 'idle', bet: '1', from: 0, stop: 0, startedAt: 0, shown: 0, last: null, lastStake: 0, lastBet: '1', returned: 0, jokerStreak: 0, history: [], lucky: false }),
  locked: (state) => state.phase === 'spinning',
  options(state) {
    if (state.phase === 'spinning') return [{ id: 'watch', label: 'Spinning...' }];
    return [
      { id: 'spin', label: state.phase === 'done' ? 'Spin again' : 'Spin' },
      { id: 'segment', label: `Bet on: ${label(state.bet)}` },
    ];
  },
  describe(state, env, highlighted) {
    if (state.phase === 'spinning') return [`Betting ${label(state.lastBet)}`, ...wheelView(state.from + state.shown)].join('\n');
    if (highlighted === 'segment') {
      const rows = SEGMENTS.map((s) => `${s.label.padEnd(6)}${String(s.count).padStart(2)}/54  pays ${s.pays}:1`);
      return rows.join('\n'); // 7 rows fill the pane; no room for a hint
    }
    const history = state.history.length ? `Last: ${state.history.slice(0, 6).map(label).join(' ')}` : '';
    const top = state.last
      ? state.returned > state.lastStake
        ? `${state.lucky ? '★ Lucky! ' : ''}${label(state.last)}! +$${state.returned - state.lastStake}`
        : `${label(state.last)}. -$${state.lastStake}`
      : `Betting on ${label(state.bet)}.`;
    return [top, ...wheelView(state.from), history].join('\n').trimEnd();
  },
  act(state, optionId, env) {
    if (state.phase === 'spinning') return { state, bankroll: env.bankroll };
    if (optionId === 'segment') {
      const i = SEGMENTS.findIndex((s) => s.id === state.bet);
      return { state: { ...state, bet: SEGMENTS[(i + 1) % SEGMENTS.length]!.id }, bankroll: env.bankroll };
    }
    if (optionId !== 'spin') return { state, bankroll: env.bankroll };
    // Every position on the wheel is equally likely, so each kind of
    // segment comes up count/54 of the time — then the ★'s luck may give a
    // losing spin a second one (the wheel just carries on to the new stop).
    const q = secondChanceRate(bigSixOutcomes(state.bet), luckOf(env));
    const { outcome: stop, lucky, first } = drawWithLuck(env.rng, () => env.rng.int(WHEEL_SIZE), (at) => WHEEL[at] !== state.bet, q);
    // A ★ second chance: the wheel first stops on the losing segment, then
    // spins again from there (its own animation starts after those frames).
    const frames = first === undefined ? [] : [...spinToFrames(state.from, first, state.bet), secondChanceFrame([`Landed on ${label(WHEEL[first]!)}`, ...wheelView(first)].join('\n'))];
    return {
      state: { ...state, phase: 'spinning', stop, lucky, from: first ?? state.from, startedAt: env.now + frames.length * FRAME_MS, shown: 0, lastStake: env.bet, lastBet: state.bet, returned: 0 },
      bankroll: env.bankroll - env.bet,
      frames,
    };
  },
  tick(state, env) {
    if (state.phase !== 'spinning') return null;
    const t = Math.max(0, (env.now - state.startedAt) / SPIN_MS);
    if (t >= 1) return settle(state, env);
    // Ease out: fast at first, slowing into the stop.
    // Rounded, so the last frames already show the real stop (no final snap).
    const shown = Math.round(travel(state.from, state.stop) * (1 - (1 - t) ** 3));
    return shown === state.shown ? null : { state: { ...state, shown }, bankroll: env.bankroll };
  },
};
